import { jcsCanonicalize } from "../protocol/jcs";
import { sha256 } from "../crypto/sha256";
import type { HubRpcInterfaceSchema } from "./hubRpcInterfaceSchema";

/**
 * Compute the interface hash: SHA-256 of the canonicalized schema, truncated
 * to 16 hex chars (64 bits of collision budget per id).
 *
 * Canonicalization:
 *   1. Strip every `comment` field (non-normative — must not affect identity).
 *      `description` is NORMATIVE and kept in the hash.
 *   2. Omit the top-level `hash` field itself.
 *   3. RFC 8785 JCS encode (recursive key sort, no whitespace) via {@link jcsCanonicalize}.
 */
export function computeInterfaceHash(schema: HubRpcInterfaceSchema): string {
    const json = jcsCanonicalize(stripNonNormative(schema, /* isRoot */ true));
    const digest = sha256(new TextEncoder().encode(json));
    const result = Array.from(digest.subarray(0, 8), (b) =>
        b.toString(16).padStart(2, "0")
    ).join("");
    return result;
}

function stripNonNormative(value: unknown, isRoot = false): unknown {
    if (value === null || typeof value !== "object") return value;
    if (Array.isArray(value)) return value.map((v) => stripNonNormative(v));

    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>)) {
        if (k === "comment" || (isRoot && k === "hash")) continue;
        const v = (value as Record<string, unknown>)[k];
        if (v === undefined) continue;
        out[k] = stripNonNormative(v);
    }
    return out;
}
